<!-- Main Content Area -->
<div class="datetime-page">
  <div class="datetime-page__container">
    <div class="datetime-page__content">

      <!-- Left: Calendar & Time Slots -->
      <div class="datetime-page__main">
        <div class="datetime-page__card">

          <ng-template #calendarShimmer>
            <div class="datetime-page__shimmer">
              <div class="datetime-page__shimmer-header">
                <div class="stencil-shimmer datetime-page__shimmer-title"></div>
                <div class="datetime-page__shimmer-nav">
                  <div class="stencil-shimmer datetime-page__shimmer-nav-btn"></div>
                  <div class="stencil-shimmer datetime-page__shimmer-nav-btn"></div>
                </div>
              </div>

              <div class="datetime-page__shimmer-grid">
                @for (i of [1,2,3,4,5,6,7]; track i) {
                  <div class="stencil-shimmer datetime-page__shimmer-weekday"></div>
                }
              </div>

              @for (row of [1,2,3,4,5]; track row) {
                <div class="datetime-page__shimmer-grid datetime-page__shimmer-grid--dates">
                  @for (i of [1,2,3,4,5,6,7]; track i) {
                    <div class="stencil-shimmer datetime-page__shimmer-date"></div>
                  }
                </div>
              }

              <hr class="datetime-page__divider" />

              <div class="datetime-page__shimmer-header">
                <div class="stencil-shimmer datetime-page__shimmer-subtitle"></div>
                <div class="stencil-shimmer datetime-page__shimmer-pill"></div>
              </div>

              <div class="datetime-page__shimmer-filters">
                @for (i of [1,2,3,4]; track i) {
                  <div class="stencil-shimmer datetime-page__shimmer-filter"></div>
                }
              </div>

              <div class="datetime-page__shimmer-slots">
                @for (i of [1,2,3,4,5,6,7,8,9]; track i) {
                  <div class="stencil-shimmer datetime-page__shimmer-slot"></div>
                }
              </div>
            </div>
          </ng-template>

          @if (!initialLoadComplete()) {
            <ng-container *ngTemplateOutlet="calendarShimmer"></ng-container>
          } @else if (isLoading()) {
            <ng-container *ngTemplateOutlet="calendarShimmer"></ng-container>
          } @else {
              <!-- ===== Real content ===== -->
              <div class="stencil-fade">

                <!-- Calendar -->
                <div class="datetime-page__calendar">
                  <div class="datetime-page__calendar-header">
                    <h2 class="datetime-page__month-title">{{ monthTitle() }}</h2>
                    <div class="datetime-page__nav-buttons">
                      <button
                        class="datetime-page__nav-btn"
                        aria-label="Previous month"
                        [disabled]="!canGoPrev()"
                        (click)="onPrevMonth()"
                      >
                        <mat-icon>chevron_left</mat-icon>
                      </button>
                      <button
                        class="datetime-page__nav-btn"
                        aria-label="Next month"
                        [disabled]="!canGoNext()"
                        (click)="onNextMonth()"
                      >
                        <mat-icon>chevron_right</mat-icon>
                      </button>
                    </div>
                  </div>

                  <!-- Week days header -->
                  <div class="datetime-page__weekdays">
                    @for (day of ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']; track day) {
                      <span class="datetime-page__weekday">{{ day }}</span>
                    }
                  </div>

                  <!-- Calendar dates with smooth collapse/expand -->
                  <div
                    class="datetime-page__calendar-body"
                    [class.datetime-page__calendar-body--collapsed]="!calendarExpanded()"
                  >
                    <div
                      class="datetime-page__calendar-weeks"
                      [style.--week-index]="selectedWeekIndex()"
                    >
                      @for (week of monthWeeks(); track $index) {
                        <div class="datetime-page__dates">
                          @for (d of week; track $index) {
                            <div
                              class="datetime-page__date"
                              [class.datetime-page__date--disabled]="d.disabled || d.day === 0"
                              [class.datetime-page__date--selected]="d.selected"
                              [class.datetime-page__date--today]="d.today && !d.selected"
                              [class.datetime-page__date--empty]="d.day === 0"
                              (click)="onDateSelect(d)"
                            >
                              {{ d.day > 0 ? d.day : '' }}
                            </div>
                          }
                        </div>
                      }
                    </div>

                    @if (noSlotsForMonth()) {
                      <div class="datetime-page__no-slots-overlay">
                        There are no time slots available for these dates
                      </div>
                    }
                  </div>

                  <div class="datetime-page__expand" (click)="toggleCalendarExpand()">
                    <mat-icon>{{ calendarExpanded() ? 'expand_less' : 'expand_more' }}</mat-icon>
                  </div>
                </div>

                <hr class="datetime-page__divider" />

                <!-- Available times -->
                <div class="datetime-page__times">
                  <div class="datetime-page__times-header">
                    <h3 class="datetime-page__times-title">Available times</h3>

                    <!-- Timezone selector -->
                    <div class="datetime-page__tz-wrapper">
                      <div class="datetime-page__timezone" (click)="openTimezoneDropdown()">
                        <mat-icon class="datetime-page__timezone-icon">language</mat-icon>
                        <span>{{ timezoneDisplay() }}</span>
                        <mat-icon class="datetime-page__timezone-arrow">arrow_drop_down</mat-icon>
                      </div>

                      @if (showTimezoneDropdown()) {
                        <div class="datetime-page__tz-backdrop" (click)="closeTimezoneDropdown()"></div>
                        <div class="datetime-page__tz-dropdown">
                          <input
                            type="text"
                            class="datetime-page__tz-search"
                            placeholder="Search timezone..."
                            [value]="timezoneSearch()"
                            (input)="onTimezoneSearchInput($event)"
                            #tzSearchInput
                          />
                          <div class="datetime-page__tz-list">
                            @for (option of filteredTimezones(); track option.timezone) {
                              <div
                                class="datetime-page__tz-option"
                                [class.datetime-page__tz-option--active]="option.timezone === timezone()"
                                (click)="selectTimezone(option.timezone)"
                              >
                                {{ option.display }}
                              </div>
                            }
                            @if (filteredTimezones().length === 0) {
                              <div class="datetime-page__tz-option datetime-page__tz-option--empty">
                                No matching timezones
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  <div class="datetime-page__times-body" [class.datetime-page__times-body--blurred]="showTimezoneDropdown()">
                  @switch (currentView()) {
                    @case ('error') {
                      <div class="datetime-page__state-message datetime-page__state-message--error">
                        An error occurred loading the meeting times
                        <button class="datetime-page__retry-btn" (click)="loadTimeSlots()">Try again</button>
                      </div>
                    }
                    @case ('no-available-dates') {
                      <div class="datetime-page__state-message">
                        There are no time slots available for these dates
                      </div>
                    }
                    @case ('select-a-date') {
                      <div class="datetime-page__state-message">
                        Select a date to see available times
                      </div>
                    }
                    @case ('select-a-time') {
                      <!-- Time filters -->
                      <div class="datetime-page__filters">
                        @for (filter of timeFilters; track filter) {
                          <button
                            class="datetime-page__filter-btn"
                            [class.datetime-page__filter-btn--active]="activeFilter() === filter"
                            (click)="onFilterChange(filter)"
                          >
                            {{ filter }}
                          </button>
                        }
                      </div>

                      <!-- Time slots grid -->
                      @if (displayTimeSlots().length > 0) {
                        <div class="datetime-page__slots">
                          @for (item of displayTimeSlots(); track $index) {
                            <button
                              class="datetime-page__slot"
                              [class.datetime-page__slot--selected]="selectedSlotIndex() === $index"
                              (click)="onTimeSlotSelect($index, item.slot)"
                            >
                              {{ item.label }}
                            </button>
                          }
                        </div>
                      } @else {
                        <div class="datetime-page__state-message">
                          No timeslots available for the selected date
                        </div>
                      }
                    }
                  }
                  </div>
                </div>

              </div>
            }
        </div>
      </div>

      <!-- Right: Cart Sidebar -->
      <div class="datetime-page__cart-wrapper">
        @if (!initialLoadComplete()) {
        <!-- Cart shimmer -->
        <div class="datetime-page__cart-shimmer">
          <div class="stencil-shimmer datetime-page__cart-shimmer-header"></div>
          <div class="datetime-page__cart-shimmer-divider"></div>
          @for (i of [1,2,3]; track i) {
            <div class="datetime-page__cart-shimmer-item">
              <div class="stencil-shimmer datetime-page__cart-shimmer-avatar"></div>
              <div class="datetime-page__cart-shimmer-text">
                <div class="stencil-shimmer datetime-page__cart-shimmer-line"></div>
                <div class="stencil-shimmer datetime-page__cart-shimmer-line datetime-page__cart-shimmer-line--short"></div>
              </div>
            </div>
          }
          <div class="datetime-page__cart-shimmer-divider"></div>
          <div class="stencil-shimmer datetime-page__cart-shimmer-total"></div>
          <div class="stencil-shimmer datetime-page__cart-shimmer-btn"></div>
        </div>
      } @else {
        <app-booking-cart
          class="stencil-fade"
          [items]="cartItems()"
          [ctaLabel]="isRescheduleMode() ? 'Reschedule' : 'Enter details'"
          [showBackButton]="canGoBack()"
          backLabel="Back"
          [readOnly]="isRescheduleMode() || cartItems().length <= 1"
          [ctaDisabled]="isCtaDisabled()"
          (ctaClick)="onEnterDetails()"
          (backClick)="onBack()"
          (removeItem)="onRemoveCartItem($event)"
        ></app-booking-cart>
      }
    </div>
    </div>
  </div>
</div>
